import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiMessageSquare } from 'react-icons/fi';
import axios from 'axios';
import { API_BASE } from '../../../api';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import '../Styles/Lessons.css';
import '../Styles/CoursePlayer.css';
import '../Styles/LmsExtra.css';
import VideoUploadModal from '../Components/VideoUploadModal';
import ImageUploadModal from '../Components/ImageUploadModal';
import ObservacionesModal from "../Modals/ObservacionesModal";
import { Models } from '../Utils/ModelsLMS';
import UrlInputModal from '../Components/UrlInputModal';
import { FiGlobe, FiEdit2 } from 'react-icons/fi';
import AlertDialog from '../Components/Shared/AlertDialog';

const formatUrl = (url) => {
    if (!url) return '';
    const trimmedUrl = url.trim();
    console.log('[formatUrl] Processing:', trimmedUrl);

    try {
        // YouTube Shorts
        if (trimmedUrl.includes('youtube.com/shorts/')) {
            const shortId = trimmedUrl.split('shorts/')[1]?.split(/[?#&]/)[0];
            if (shortId && shortId.length === 11) {
                return `https://www.youtube.com/embed/${shortId}`;
            }
        }

        // YouTube Standard & Embed & youtu.be
        const ytRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
        const ytMatch = trimmedUrl.match(ytRegex);
        if (ytMatch && ytMatch[1]) {
            return `https://www.youtube.com/embed/${ytMatch[1]}`;
        }

        // Vimeo
        const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
        const vimeoMatch = trimmedUrl.match(vimeoRegex);
        if (vimeoMatch && vimeoMatch[1]) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }
    } catch (e) {
        console.error('[formatUrl] Error:', e);
    }

    return trimmedUrl;
};

const Lessons = () => {
    const [sections, setSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [selectedElement, setSelectedElement] = useState(null);
    const [jsonInput, setJsonInput] = useState('');

    // Estados para integración con BD
    const [lessons, setLessons] = useState([]);
    const [modulos, setModulos] = useState([]);
    const [moduloContenido, setModuloContenido] = useState([]); // Para orden correcto de lecciones
    const [selectedLessonId, setSelectedLessonId] = useState('');
    const [currentCourseId, setCurrentCourseId] = useState(null); // Nuevo estado para contexto
    const [contentTypes, setContentTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

    // Estados para crear nueva lección
    const [showModal, setShowModal] = useState(false);
    const [newLesson, setNewLesson] = useState({ titulo: '', idModulo: '', monedas: 10 });

    // Estado para editar título de lección
    const [lessonTitle, setLessonTitle] = useState('');

    // Estados para drag & drop de sections
    const [draggingSection, setDraggingSection] = useState(null);
    const [dragOverSection, setDragOverSection] = useState(null);

    // Estados para resize de elementos
    const [resizing, setResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null);
    const [initialResizeState, setInitialResizeState] = useState(null);

    // Estados para manejo de videos
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [videoTargetElementId, setVideoTargetElementId] = useState(null);

    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imageTargetElementId, setImageTargetElementId] = useState(null);

    // Estados para Iframes
    const [urlModalOpen, setUrlModalOpen] = useState(false);
    const [urlTargetElementId, setUrlTargetElementId] = useState(null);

    const [propertiesOpen, setPropertiesOpen] = useState(true);

    // Context Menu State
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
    const contextMenuRef = useRef(null);

    // Auto-save refs and state
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const prevSectionsRef = useRef('');
    const prevTitleRef = useRef('');
    const isFirstLoadRef = useRef(true);
    const isSavingRef = useRef(false); // Ref para evitar guardados concurrentes
    const [alertConfig, setAlertConfig] = useState({
        open: false,
        message: '',
        title: 'Información',
        variant: 'info',
        onConfirm: null,
        onCancel: null,
        confirmText: 'Aceptar',
        cancelText: 'Cancelar'
    });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setAlertConfig({
            open: true,
            message,
            variant,
            title,
            onConfirm: null,
            onCancel: null,
            confirmText: 'Aceptar'
        });
    };

    const triggerConfirm = useCallback(({ title = 'Confirmar', message, onConfirm, onCancel, confirmText = 'Aceptar', cancelText = 'Cancelar', variant = 'warning' }) => {
        setAlertConfig({
            open: true,
            title,
            message,
            onConfirm,
            onCancel,
            confirmText,
            cancelText,
            variant
        });
    }, []);

    const canvasRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const hideNavigation = location.state?.hideNavigation;

    const isNavigatingRef = useRef(false);
    const hasPushedStateRef = useRef(false);

    const isDirty = useMemo(() => {
        if (isFirstLoadRef.current) return false;

        const currentSectionsStr = JSON.stringify(sections);
        const sectionsChanged = currentSectionsStr !== prevSectionsRef.current;

        const currentTitleTrimmed = lessonTitle.trim();
        const prevTitleTrimmed = (prevTitleRef.current || '').trim();
        const titleChanged = currentTitleTrimmed !== prevTitleTrimmed;

        return sectionsChanged || titleChanged;
    }, [sections, lessonTitle]);

    // Bloqueo para refrescar/cerrar pestaña
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty || isAutoSaving || isSavingRef.current) {
                e.preventDefault();
                e.returnValue = "Tienes cambios sin guardar. ¿Estás seguro de que quieres salir?";
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isAutoSaving]);

    // Gestión del "trap" en el historial (Evita acumulación)
    useEffect(() => {
        const needsGuard = isDirty || isAutoSaving || isSavingRef.current;

        // Si ya NO necesita guardia pero tenemos un trap activo, lo quitamos silenciosamente
        if (!needsGuard && hasPushedStateRef.current) {
            isNavigatingRef.current = true;
            hasPushedStateRef.current = false;
            window.history.back(); // Quitamos el estado extra que pusimos
            return;
        }

        // Si necesita guardia y NO tenemos trap, lo creamos UNA SOLA VEZ
        if (needsGuard && !hasPushedStateRef.current) {
            window.history.pushState(null, null, window.location.pathname);
            hasPushedStateRef.current = true;
        }
    }, [isDirty, isAutoSaving]);

    // Bloqueo para el botón de "Atrás" del navegador
    useEffect(() => {
        const handlePopState = (e) => {
            // Si es una navegación controlada por nosotros, ignoramos
            if (isNavigatingRef.current) {
                isNavigatingRef.current = false;
                return;
            }

            if (hasPushedStateRef.current) {
                triggerConfirm({
                    title: 'Cambios sin guardar',
                    message: "⚠️ Tienes cambios sin guardar o el proceso de guardado no ha terminado. ¿Estás seguro de que deseas salir?",
                    onConfirm: () => {
                        hasPushedStateRef.current = false;
                        isNavigatingRef.current = true;
                        window.history.back();
                    },
                    onCancel: () => {
                        // El usuario canceló. Volvemos al estado del trap (adelante).
                        window.history.forward();
                    },
                    variant: 'warning'
                });
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []); // No dependencias para evitar reinicios ruidosos

    // Función auxiliar para navegar internamente
    const safeNavigate = useCallback((to, options = {}) => {
        const needsGuard = isDirty || isAutoSaving || isSavingRef.current;

        const proceed = () => {
            if (hasPushedStateRef.current) {
                isNavigatingRef.current = true;
                hasPushedStateRef.current = false;
                window.history.back();
                setTimeout(() => {
                    navigate(to, { ...options, replace: true });
                }, 50);
            } else {
                isNavigatingRef.current = true;
                navigate(to, { ...options, replace: true });
            }
        };

        if (needsGuard) {
            triggerConfirm({
                title: 'Cambios sin guardar',
                message: "⚠️ Hay cambios sin guardar o el proceso de guardado no ha terminado. ¿Estás seguro de que deseas salir?",
                onConfirm: proceed,
                variant: 'warning'
            });
        } else {
            proceed();
        }
    }, [isDirty, isAutoSaving, navigate, triggerConfirm]);
    // ----------------------------

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');

    const VIDEO_DELETE_ENDPOINT = `${SERVER_URL}/api/upload/lesson-video`;
    const IMAGE_DELETE_ENDPOINT = `${SERVER_URL}/api/upload/lesson-image`;

    // Cerrar context menu al hacer click en cualquier lado
    useEffect(() => {
        const handleClick = (e) => {
            if (contextMenu.visible) {
                if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) {
                    return;
                }
                setContextMenu(prev => ({ ...prev, visible: false }));
            }
        };
        window.addEventListener('click', handleClick, true);
        const handleRightClick = (e) => {
            if (contextMenu.visible) {
                if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                    setContextMenu(prev => ({ ...prev, visible: false }));
                }
            }
        }
        window.addEventListener('contextmenu', handleRightClick, true);
        return () => {
            window.removeEventListener('click', handleClick, true);
            window.removeEventListener('contextmenu', handleRightClick, true);
        };
    }, [contextMenu.visible]);

    // Ajustar posición del context menu
    useEffect(() => {
        if (contextMenu.visible && contextMenuRef.current) {
            const rect = contextMenuRef.current.getBoundingClientRect();
            let newX = contextMenu.x;
            let newY = contextMenu.y;
            const margin = 20;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            if (contextMenu.x + rect.width > viewportWidth) newX = viewportWidth - rect.width - margin;
            if (contextMenu.y + rect.height > viewportHeight) newY = viewportHeight - rect.height - margin;

            newX = Math.max(margin, newX);
            newY = Math.max(margin, newY);

            if (newX !== contextMenu.x || newY !== contextMenu.y) {
                setContextMenu(prev => ({ ...prev, x: newX, y: newY }));
            }
        }
    }, [contextMenu.visible, contextMenu.type, contextMenu.elementId]);

    // Cargar catálogos iniciales y recuperar sesión
    useEffect(() => {
        const fetchData = async () => {
            try {
                const typesRes = await axios.get(LMS_ENDPOINTS.contenidoTipo);
                setContentTypes(typesRes.data || []);

                if (location.state?.lessonId) {
                    setSelectedLessonId(location.state.lessonId);
                    localStorage.setItem('lms_lastEditedLessonId', location.state.lessonId);
                } else {
                    const savedId = localStorage.getItem('lms_lastEditedLessonId');
                    if (savedId) {
                        console.log("Restoring session for lesson:", savedId);
                        setSelectedLessonId(savedId);
                    }
                }
            } catch (error) {
                console.error("Error loading initial data:", error);
            }
        };
        fetchData();
    }, []);

    // Persistir ID de lección al cambiar
    useEffect(() => {
        if (selectedLessonId) {
            localStorage.setItem('lms_lastEditedLessonId', selectedLessonId);
        }
    }, [selectedLessonId]);

    // Cargar contenido de la lección seleccionada
    useEffect(() => {
        if (!selectedLessonId) {
            setSections([]);
            return;
        }

        const loadLessonContent = async () => {
            setLoading(true);
            try {
                const response = await axios.get(LMS_ENDPOINTS.leccionesContenidoByLeccion(selectedLessonId));
                const lessonContent = (response.data || []).filter(c => (c.activo !== undefined ? c.activo : c.Activo));

                const loadedSections = lessonContent.map(item => {
                    try {
                        const valor = item.valor || item.Valor;
                        const idContenido = item.idContenido || item.IdContenido;

                        if (!valor) return null;
                        const parsedValue = JSON.parse(valor);
                        return {
                            ...parsedValue,
                            id: idContenido,
                            dbId: idContenido
                        };
                    } catch (e) {
                        console.error("Error parsing content value:", e);
                        return null;
                    }
                }).filter(el => el !== null).sort((a, b) => (a.order || 0) - (b.order || 0));

                setSections(loadedSections);
                prevSectionsRef.current = JSON.stringify(loadedSections);

                const currentLesson = lessons.find(l => l.idLeccion === parseInt(selectedLessonId));
                if (currentLesson) {
                    const title = currentLesson.titulo || currentLesson.Titulo || '';
                    setLessonTitle(title);
                    prevTitleRef.current = title;
                }

                setTimeout(() => { isFirstLoadRef.current = false; }, 1000);
            } catch (error) {
                console.error("Error loading lesson content:", error);
            } finally {
                setLoading(false);
            }
        };

        loadLessonContent();
    }, [selectedLessonId]);

    // Actualizar el título local y contexto
    useEffect(() => {
        const fetchLessonContext = async () => {
            if (!selectedLessonId) {
                setLessonTitle('');
                return;
            }
            try {
                const resLeccion = await axios.get(LMS_ENDPOINTS.leccionesById(selectedLessonId));
                const leccionData = Array.isArray(resLeccion.data) ? resLeccion.data[0] : resLeccion.data;

                if (!leccionData) return;

                const title = leccionData.titulo || leccionData.Titulo || '';
                setLessonTitle(title);
                prevTitleRef.current = title; // Sincronizar ref inicial

                const idModulo = leccionData.idModulo || leccionData.IdModulo;
                if (!idModulo) return;

                let idCurso = null;
                const moduloEnMemoria = modulos.find(m => (m.idModulo || m.IdModulo) === idModulo);

                if (moduloEnMemoria) {
                    idCurso = moduloEnMemoria.idCurso || moduloEnMemoria.IdCurso;
                } else {
                    const resModulo = await axios.get(LMS_ENDPOINTS.moduloById(idModulo));
                    const moduloData = Array.isArray(resModulo.data) ? resModulo.data[0] : resModulo.data;
                    if (moduloData) idCurso = moduloData.idCurso || moduloData.IdCurso;
                }

                if (idCurso && idCurso !== currentCourseId) {
                    const [resLeccionesCurso, resModulosCurso, resContenidoCurso] = await Promise.all([
                        axios.get(LMS_ENDPOINTS.leccionesByCurso(idCurso)),
                        axios.get(LMS_ENDPOINTS.moduloByCurso(idCurso)),
                        axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(idCurso))
                    ]);

                    setLessons(resLeccionesCurso.data || []);
                    setModulos(resModulosCurso.data || []);
                    setModuloContenido((resContenidoCurso.data || []).map(Models.moduloContenido));
                    setCurrentCourseId(idCurso);
                }

            } catch (error) {
                console.warn("Error cargando contexto de lección:", error);
            }
        };
        fetchLessonContext();
    }, [selectedLessonId, currentCourseId]);

    // Crear nueva lección
    const handleCreateLesson = async () => {
        if (!newLesson.titulo.trim()) {
            triggerAlert('El título es obligatorio', 'warning', 'Atención');
            return;
        }
        if (!newLesson.idModulo) {
            triggerAlert('Debes seleccionar un módulo.', 'warning', 'Atención');
            return;
        }

        setLoading(true);
        try {
            let monedasParaLeccion = parseInt(newLesson.monedas) || 0;
            try {
                const moduloSeleccionado = modulos.find(m => m.idModulo === parseInt(newLesson.idModulo));
                if (moduloSeleccionado && moduloSeleccionado.idCurso) {
                    const [respLecciones, respModulos] = await Promise.all([
                        axios.get(LMS_ENDPOINTS.lecciones),
                        axios.get(LMS_ENDPOINTS.modulo)
                    ]);
                    const todasLecciones = respLecciones.data || [];
                    const todosModulos = respModulos.data || [];
                    const idsModulosDelCurso = todosModulos
                        .filter(m => (m.idCurso || m.IdCurso) === moduloSeleccionado.idCurso)
                        .map(m => m.idModulo || m.IdModulo);
                    const leccionConMonedas = todasLecciones.find(l =>
                        idsModulosDelCurso.includes(l.idModulo || l.IdModulo) &&
                        (l.monedas || l.Monedas || 0) > 0
                    );
                    if (leccionConMonedas) {
                        monedasParaLeccion = leccionConMonedas.monedas || leccionConMonedas.Monedas || 0;
                    }
                }
            } catch (error) {
                console.warn('Warning getting course info', error);
            }

            const payload = {
                idModulo: parseInt(newLesson.idModulo),
                titulo: newLesson.titulo,
                monedas: monedasParaLeccion,
                activo: true
            };

            const response = await axios.post(LMS_ENDPOINTS.lecciones, payload);

            if (response.status === 200 || response.status === 201) {
                const createdId = response.data;
                const lessonsRes = await axios.get(LMS_ENDPOINTS.lecciones);
                const lessonsData = (lessonsRes.data || []).sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));
                setLessons(lessonsData);
                const newLessonObj = lessonsData.find(l => l.idLeccion === createdId) || lessonsData[lessonsData.length - 1];
                if (newLessonObj) {
                    setSelectedLessonId(newLessonObj.idLeccion);
                }
                setShowModal(false);
                setNewLesson({ titulo: '', idModulo: modulos.length > 0 ? modulos[0].idModulo : '', monedas: 10 });
                triggerAlert('Lección creada exitosamente', 'success', 'Éxito');
            }
        } catch (error) {
            console.error('Error creating lesson:', error);
            triggerAlert('Error al crear la lección: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Guardar título de lección
    const handleSaveLessonTitle = async (silent = false) => {
        if (!selectedLessonId || !lessonTitle.trim()) {
            if (!silent) triggerAlert('El título no puede estar vacío', 'warning', 'Atención');
            return;
        }

        // Si ya hay un guardado general en curso, delegamos a ese para evitar conflictos
        // aunque esta función suele ser llamada aisladamente o dentro de una secuencia controlada
        const amISavingLocally = !isSavingRef.current;
        if (amISavingLocally) isSavingRef.current = true;

        if (!silent) setLoading(true);
        else setIsAutoSaving(true);

        try {
            const currentLesson = lessons.find(l => (l.idLeccion || l.IdLeccion) === parseInt(selectedLessonId));
            if (!currentLesson) return;

            const payload = {
                idLeccion: parseInt(selectedLessonId),
                idModulo: currentLesson.idModulo || currentLesson.IdModulo,
                titulo: lessonTitle.trim(),
                monedas: currentLesson.monedas || currentLesson.Monedas || 0,
                activo: (currentLesson.activo !== undefined ? currentLesson.activo : currentLesson.Activo) ?? true
            };

            await axios.put(LMS_ENDPOINTS.lecciones, payload);

            // Sincronizar ref inicial
            prevTitleRef.current = lessonTitle.trim();

            if (!silent) console.log('Título de lección actualizado exitosamente');
        } catch (error) {
            console.error('Error al actualizar título de lección:', error);
            if (!silent) triggerAlert('Error al actualizar el título: ' + error.message);
        } finally {
            if (amISavingLocally) isSavingRef.current = false;
            if (!silent) setLoading(false);
            else setIsAutoSaving(false);
        }
    };

    // Guardar en Base de Datos
    const handleSaveToDb = async (silent = false) => {
        if (!selectedLessonId) {
            if (!silent) triggerAlert("Selecciona una lección primero", 'warning', 'Atención');
            return;
        }

        if (isSavingRef.current) return;
        isSavingRef.current = true;

        console.log("Iniciando guardado de contenido...");

        if (!silent) setLoading(true);
        else setIsAutoSaving(true);

        try {
            // 1. Obtener contenido existente para determinar bajas
            const response = await axios.get(LMS_ENDPOINTS.leccionesContenidoByLeccion(selectedLessonId));
            const existingContent = response.data || [];

            // Identificar qué IDs de BD ya no están en nuestro estado local
            const currentDbIds = sections.map(s => s.dbId).filter(id => id);
            const toDelete = existingContent.filter(c => {
                const id = c.idContenido || c.IdContenido;
                return id && !currentDbIds.includes(id);
            });

            // Borrar secciones eliminadas
            for (const item of toDelete) {
                const idToDelete = item.idContenido || item.IdContenido;
                await axios.delete(LMS_ENDPOINTS.leccionesContenidoById(idToDelete));
            }

            // 2. Guardar secciones actuales (Update o Create)
            // Hacemos una copia local para manipular IDs sin esperar a múltiples renders rápidos
            const updatedSections = [...sections];

            for (let i = 0; i < updatedSections.length; i++) {
                const section = updatedSections[i];
                const payload = {
                    idContenido: section.dbId ? parseInt(section.dbId) : 0,
                    idLeccion: parseInt(selectedLessonId),
                    valor: JSON.stringify({
                        type: section.type,
                        columns: section.columns,
                        splitRatio: section.splitRatio,
                        order: i, // Usar el índice actual como orden
                        style: section.style,
                        children: section.children,
                    }),
                    idContenidoTipo: 1,
                    activo: true,
                };

                if (section.dbId) {
                    await axios.put(LMS_ENDPOINTS.leccionesContenido, payload);
                } else {
                    const res = await axios.post(LMS_ENDPOINTS.leccionesContenido, payload);
                    if (res.data) {
                        // El backend devuelve el nuevo ID
                        updatedSections[i] = {
                            ...updatedSections[i],
                            dbId: res.data,
                            // Si el ID era temporal (Date.now), lo reemplazamos por el de BD para normalizar,
                            // pero solo si no estamos en medio de una edición crítica.
                            // Para evitar el "leap", si ya es el seleccionado, actualizamos la referencia de selección.
                        };
                    }
                }
            }

            // 3. Actualizar estado local de forma inteligente sin cambiar los IDs locales
            setSections(prev => {
                // Mapeamos solo el dbId nuevo a las secciones actuales para que la UI no se entere
                // de que el ID cambió en la base de datos (mantenemos el ID temporal estable)
                return prev.map((s, idx) => {
                    const match = updatedSections.find(us => us.order === idx);
                    if (match && !s.dbId && match.dbId) {
                        return { ...s, dbId: match.dbId }; 
                        // NOTA: NO cambiamos s.id para evitar el desmonte de componentes
                    }
                    return s;
                });
            });

            // No necesitamos re-mapear la selección si el id se mantuvo estable
            
            // Actualizar ref de comparación para auto-save
            // Debemos asegurarnos de que la ref tenga los dbIds para futuras comparaciones
            prevSectionsRef.current = JSON.stringify(updatedSections.map((us, idx) => {
                const s = sections[idx];
                return { ...us, id: s.id }; // Mantener el id original en la comparación
            }));

            console.log("Guardado de contenido exitoso.");
        } catch (error) {
            console.error("Error saving content to DB:", error);
            if (!silent) triggerAlert("Error al guardar: " + error.message);
        } finally {
            isSavingRef.current = false;
            if (!silent) setLoading(false);
            else setIsAutoSaving(false);
        }
    };

    // Auto-save Effect
    useEffect(() => {
        if (isFirstLoadRef.current) return;
        if (loading || isSavingRef.current) return;

        const currentSectionsStr = JSON.stringify(sections);
        const sectionsChanged = currentSectionsStr !== prevSectionsRef.current;
        const titleChanged = lessonTitle.trim() !== (prevTitleRef.current || '').trim();

        if (!sectionsChanged && !titleChanged) return;

        // Mostrar indicador inmediatamente
        setIsAutoSaving(true);

        const timer = setTimeout(async () => {
            if (isSavingRef.current) return;

            try {
                // Ejecutamos de forma secuencial y controlada para evitar race conditions
                if (sectionsChanged) {
                    await handleSaveToDb(true);
                }
                
                // Si el título cambió, lo guardamos después
                // (O viceversa, el orden no importa tanto como que sean secuenciales)
                if (titleChanged) {
                    await handleSaveLessonTitle(true);
                }
            } catch (error) {
                console.error("Auto-save failed:", error);
            } finally {
                setIsAutoSaving(false);
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, [sections, lessonTitle, loading]);

    // Agregar nueva section
    const addSection = (columnCount) => {
        const newSection = {
            id: Date.now(),
            dbId: null,
            type: 'section',
            columns: columnCount,
            splitRatio: 50,
            order: sections.length,
            style: {
                padding: 20,
                backgroundColor: '#ffffff',
                gap: 20
            },
            children: Array.from({ length: columnCount }, (_, i) => ({
                type: 'column',
                columnIndex: i,
                children: []
            }))
        };
        setSections(prev => [...prev, newSection]);
        setSelectedSection(newSection.id);
        setSelectedColumn(0);
        setSelectedElement(null);
    };

    // Helper para buscar un elemento por ID en todas las secciones
    const findElementById = (elementId) => {
        for (const section of sections) {
            for (const column of section.children || []) {
                const found = (column.children || []).find((el) => el.id === elementId);
                if (found) return found;
            }
        }
        return null;
    };

    // Agregar elemento a columna
    const addElementToColumn = (elementType) => {
        if (selectedSection === null || selectedColumn === null) {
            triggerAlert('Selecciona una columna primero', 'warning', 'Atención');
            return;
        }

        let content = '';
        let fontSize = 16;
        let fontWeight = 'normal';
        let width = '100%';
        let height = 'auto';

        if (elementType === 'text') {
            content = 'Texto Nuevo';
        } else if (elementType === 'h1') {
            content = 'Título Principal';
            fontSize = 32;
            fontWeight = 'bold';
        } else if (elementType === 'h2') {
            content = 'Subtítulo';
            fontSize = 24;
            fontWeight = 'bold';
        } else if (elementType === 'image') {
            height = 'auto';
            width = '100%';
        } else if (elementType === 'video') {
            height = 'auto';
        }



        const newElement = {
            id: Date.now(),
            type: elementType,
            content,
            src: elementType === 'image' ? '/favicon.ico' : elementType === 'video' ? '' : '',
            style: {
                width,
                height,
                color: '#000000',
                fontSize,
                fontWeight,
                padding: 10,
                textAlign: 'justify',
                marginBottom: 10
            }
        };

        setSections(prev => prev.map(section => {
            if (section.id === selectedSection) {
                return {
                    ...section,
                    children: section.children.map((col, idx) => {
                        if (idx === (selectedColumn ?? 0)) {
                            return {
                                ...col,
                                children: [...col.children, newElement]
                            };
                        }
                        return col;
                    })
                };
            }
            return section;
        }));

        setSelectedElement(newElement.id);

        // Si es un video, abrir el modal de subida para asignar src
        if (elementType === 'video') {
            setVideoTargetElementId(newElement.id);
            setVideoModalOpen(true);
        }

        // Si es una imagen, abrir el modal de subida para asignar src
        if (elementType === 'image') {
            setImageTargetElementId(newElement.id);
            setImageModalOpen(true);
        }

        if (elementType === 'iframe') {
            setUrlTargetElementId(newElement.id);
            setUrlModalOpen(true);
        }
    };

    // Drag & Drop de sections
    const handleSectionDragStart = (e, sectionId) => {
        setDraggingSection(sectionId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleSectionDragOver = (e, sectionId) => {
        e.preventDefault();
        if (draggingSection !== sectionId) {
            setDragOverSection(sectionId);
        }
    };

    const handleSectionDrop = (e, targetSectionId) => {
        e.preventDefault();

        if (draggingSection === targetSectionId) {
            setDraggingSection(null);
            setDragOverSection(null);
            return;
        }

        const dragIndex = sections.findIndex(s => s.id === draggingSection);
        const dropIndex = sections.findIndex(s => s.id === targetSectionId);

        const newSections = [...sections];
        const [removed] = newSections.splice(dragIndex, 1);
        newSections.splice(dropIndex, 0, removed);

        newSections.forEach((s, idx) => {
            s.order = idx;
        });

        setSections(newSections);
        setDraggingSection(null);
        setDragOverSection(null);
    };

    // Resizing State
    const [resizingType, setResizingType] = useState(null); // 'element' | 'column'

    // Resize de elementos
    const handleResizeStart = (e, elementId, direction) => {
        e.stopPropagation();

        const element = getSelectedElement();
        if (!element) return;

        const elementDiv = e.target.parentElement;
        const columnDiv = elementDiv.parentElement;
        const sectionDiv = columnDiv.parentElement; // Grid container

        setResizing(true);
        setResizeDirection(direction);
        setResizingType('element');

        setInitialResizeState({
            startX: e.clientX,
            startY: e.clientY,
            startWidth: elementDiv.offsetWidth,
            startHeight: elementDiv.offsetHeight,
            containerWidth: columnDiv.offsetWidth,
            sectionWidth: sectionDiv.offsetWidth,
            sectionId: selectedSection,
            columnIndex: selectedColumn
        });
    };

    const handleMouseMove = (e) => {
        if (!resizing || !initialResizeState) return;

        // Solo manejamos redimensionamiento de elementos
        if (resizingType === 'element' && selectedElement) {
            const deltaX = e.clientX - initialResizeState.startX;
            const deltaY = e.clientY - initialResizeState.startY;

            const { startWidth, startHeight, containerWidth, sectionWidth, sectionId, columnIndex } = initialResizeState;
            const element = getSelectedElement();
            const section = sections.find(s => s.id === sectionId);

            let newStyle = {};

            // Lógica Especial: Si estamos en la Columna 0 de una sección de 2 columnas,
            // el redimensionamiento controla el ancho de la columna (splitRatio).
            if (resizeDirection.includes('e') && section && section.columns === 2 && columnIndex === 0) {
                const newWidthPx = Math.max(50, startWidth + deltaX);
                const newSplitRatio = (newWidthPx / sectionWidth) * 100;

                // Clamp entre 25% y 75%
                const clampedRatio = Math.max(25, Math.min(75, newSplitRatio));

                // Actualizamos simultáneamente el estilo del elemento (para que llene la columna)
                // y el ratio de la sección
                setSections(prevSections => prevSections.map(s => {
                    if (s.id === sectionId) {
                        return {
                            ...s,
                            splitRatio: clampedRatio,
                            children: s.children.map((col, cIdx) => ({
                                ...col,
                                children: col.children.map(el =>
                                    el.id === selectedElement
                                        ? { ...el, style: { ...el.style, width: '100%' } }
                                        : el
                                )
                            }))
                        };
                    }
                    return s;
                }));
                return; // Salimos temprano ya que manejamos estado completo aquí
            }

            // Comportamiento Normal (resize dentro de la columna)
            if (resizeDirection.includes('e') || resizeDirection.includes('w')) {
                const newWidthPx = Math.max(50, startWidth + (resizeDirection.includes('e') ? deltaX : -deltaX));

                // Calcular porcentaje basado en el ancho del contenedor
                const safeContainerWidth = containerWidth - 4;
                const minWidth = (element && ['video', 'image', 'iframe'].includes(element.type)) ? 10 : 5;
                const newWidthPercent = Math.min(100, Math.max(minWidth, (newWidthPx / safeContainerWidth) * 100));

                newStyle.width = `${newWidthPercent.toFixed(2)}%`;

                // Solo forzamos height auto en texto. Videos, Imágenes e Iframes pueden tener alto custom.
                if (element && (element.type === 'text' || element.type === 'h1' || element.type === 'h2')) {
                    newStyle.height = 'auto';
                }
            }

            if (resizeDirection.includes('s') || resizeDirection.includes('n')) {
                const newHeight = Math.max(30, startHeight + (resizeDirection.includes('s') ? deltaY : -deltaY));
                newStyle.height = `${newHeight}px`;
            }

            updateElementStyle(selectedElement, newStyle);
        }
    };

    const handleMouseUp = () => {
        setResizing(false);
        setResizeDirection(null);
        setResizingType(null);
        setInitialResizeState(null);
    };

    // Actualizar elemento ('updateElement' function remains unchanged...)
    const updateElement = (elementId, updates) => {
        setSections(prev => prev.map(section => ({
            ...section,
            children: section.children.map(col => ({
                ...col,
                children: col.children.map(el =>
                    el.id === elementId ? { ...el, ...updates } : el
                )
            }))
        })));
    };

    const updateElementStyle = (elementId, styleUpdates) => {
        if (styleUpdates.fontSize) {
            styleUpdates.fontSize = Math.min(250, parseInt(styleUpdates.fontSize) || 0);
        }

        setSections(prev => prev.map(section => ({
            ...section,
            children: section.children.map(col => ({
                ...col,
                children: col.children.map(el =>
                    el.id === elementId ? { ...el, style: { ...el.style, ...styleUpdates } } : el
                )
            }))
        })));
    };

    // Eliminar elemento
    const deleteElement = async (elementId) => {
        // ... (resto del código sin cambios)
        // 1️⃣ Buscar elemento para ver si tiene archivo S3
        let elementToDelete = null;
        for (const sec of sections) {
            for (const col of sec.children) {
                const found = col.children.find(e => e.id === elementId);
                if (found) {
                    elementToDelete = found;
                    break;
                }
            }
            if (elementToDelete) break;
        }

        // 2️⃣ Si tiene archivo en S3, borrarlo
        if (elementToDelete && elementToDelete.src && elementToDelete.src.includes('s3.amazonaws.com')) {
            console.log('[Lessons] 🗑️ Eliminando archivo de S3:', elementToDelete.src);

            let deleteEndpoint;
            if (elementToDelete.type === 'video') {
                deleteEndpoint = VIDEO_DELETE_ENDPOINT;
            } else if (elementToDelete.type === 'image') {
                deleteEndpoint = IMAGE_DELETE_ENDPOINT;
            }

            if (deleteEndpoint) {
                try {
                    const deleteResponse = await fetch(
                        `${deleteEndpoint}?filePath=${encodeURIComponent(elementToDelete.src)}`,
                        { method: 'DELETE' }
                    );

                    if (deleteResponse.ok) {
                        console.log('[Lessons] ✅ Archivo eliminado de S3');
                    }
                } catch (error) {
                    console.warn('[Lessons] ⚠️ Error al eliminar archivo de S3:', error);
                }
            }
        }

        // 3️⃣ Eliminar del estado (UI)
        setSections(prev => prev.map(section => {
            // No filtramos por selectedSection aquí por si acaso, 
            // solo buscamos el elemento en todas las secciones para mayor seguridad
            return {
                ...section,
                children: section.children.map(col => ({
                    ...col,
                    children: col.children.filter(el => el.id !== elementId)
                }))
            };
        }));
        setSelectedElement(null);
    };

    // Eliminar section
    const deleteSection = async (sectionId) => {
        // Encontrar la sección
        const sectionToDelete = sections.find(s => s.id === sectionId);

        if (!sectionToDelete) return;

        // Recorrer todos los elementos de la sección para encontrar archivos de S3
        const elementsToDelete = [];

        sectionToDelete.children.forEach(column => {
            column.children.forEach(element => {
                if ((element.type === 'video' || element.type === 'image') &&
                    element.src &&
                    element.src.includes('s3.amazonaws.com')) {
                    elementsToDelete.push(element);
                }
            });
        });

        // Eliminar archivos de S3 si existen
        if (elementsToDelete.length > 0) {
            console.log(`[Lessons] 🗑️ Eliminando ${elementsToDelete.length} archivos de S3 asociados a la sección...`);

            // Procesar eliminaciones en paralelo
            const deletePromises = elementsToDelete.map(async (element) => {
                let deleteEndpoint;
                if (element.type === 'video') {
                    deleteEndpoint = VIDEO_DELETE_ENDPOINT;
                } else if (element.type === 'image') {
                    deleteEndpoint = IMAGE_DELETE_ENDPOINT;
                }

                if (deleteEndpoint) {
                    try {
                        const deleteResponse = await fetch(
                            `${deleteEndpoint}?filePath=${encodeURIComponent(element.src)}`,
                            { method: 'DELETE' }
                        );
                        if (deleteResponse.ok) {
                            console.log(`[Lessons] ✅ Archivo eliminado de S3: ${element.src}`);
                        }
                    } catch (error) {
                        console.warn(`[Lessons] ⚠️ Error al eliminar archivo de S3 (${element.src}):`, error);
                    }
                }
            });

            await Promise.all(deletePromises);
        }

        // Eliminar la sección del estado
        setSections(sections.filter(s => s.id !== sectionId).map((s, idx) => ({ ...s, order: idx })));

        if (selectedSection === sectionId) {
            setSelectedSection(null);
            setSelectedColumn(null);
            setSelectedElement(null);
        }
    };


    // Obtener elemento seleccionado
    const getSelectedElement = () => {
        if (!selectedSection || selectedColumn === null || !selectedElement) return null;

        const section = sections.find(s => s.id === selectedSection);
        if (!section || !section.children) return null;

        const column = section.children[selectedColumn];
        if (!column || !column.children) return null;

        return column.children.find(el => el.id === selectedElement);
    };

    const currentElement = getSelectedElement();

    // Cuando el modal de video termina de subir, actualizamos el src del elemento objetivo
    const handleVideoUploaded = (filePath) => {
        if (!videoTargetElementId || !filePath) return;
        updateElement(videoTargetElementId, { src: filePath });
        setVideoTargetElementId(null);
    };

    // Cuando el modal de imagen termina de subir, actualizamos el src del elemento objetivo
    const handleImageUploaded = (filePath) => {
        if (!imageTargetElementId || !filePath) return;
        updateElement(imageTargetElementId, { src: filePath });
        setImageTargetElementId(null);
    };

    const handleBack = () => {
        if (currentCourseId) {
            safeNavigate(`/menu/lms/curso/${currentCourseId}`);
        } else {
            safeNavigate(-1);
        }
    };

    const handleContextMenu = (e, sectionId, colIdx) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedSection(sectionId);
        setSelectedColumn(colIdx);
        setContextMenu({ visible: true, x: e.clientX, y: e.clientY, type: 'column' });
    };

    const handleElementContextMenu = (e, elementId, sectionId, colIdx) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type: 'element',
            elementId,
            sectionId,
            colIdx
        });
    };

    const handleNavigateLesson = (direction) => {
        if (!selectedLessonId) return;

        const currentLesson = lessons.find(l => (l.idLeccion || l.IdLeccion) == selectedLessonId);
        if (!currentLesson) return;

        // Obtener el módulo de la lección actual para saber el curso
        const currentModule = modulos.find(m => (m.idModulo || m.IdModulo) === (currentLesson.idModulo || currentLesson.IdModulo));
        if (!currentModule) {
            console.warn('⚠️ Módulo no encontrado para la lección');
            return;
        }

        // Obtener todos los módulos del curso actual, ordenados
        const currentCourseModules = modulos
            .filter(m => (m.idCurso || m.IdCurso) === (currentModule.idCurso || currentModule.IdCurso))
            .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

        // Obtener TODAS las lecciones del curso en orden
        const todasLeccionesDelCurso = [];

        for (const modulo of currentCourseModules) {
            const leccionesDelModulo = moduloContenido
                .filter(mc => {
                    const idModulo = mc.idModulo || mc.IdModulo;
                    const tablaContenido = mc.tablaContenido || mc.TablaContenido;
                    const activo = mc.activo !== undefined ? mc.activo : mc.Activo;

                    const isCorrectModule = idModulo === (modulo.idModulo || modulo.IdModulo);
                    const isLesson = tablaContenido && tablaContenido.toLowerCase() === 'lecciones';
                    const isActive = activo;
                    return isCorrectModule && isLesson && isActive;
                })
                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0))
                .map(mc => mc.idAsociado || mc.IdAsociado);

            todasLeccionesDelCurso.push(...leccionesDelModulo);
        }

        console.log('🔍 Debug navegación:', {
            currentLessonId: selectedLessonId,
            currentModuleId: currentLesson.idModulo,
            currentCourseId: currentModule.idCurso,
            todasLeccionesDelCurso,
            direction
        });

        // Encontrar índice actual en el orden correcto
        const currentIndex = todasLeccionesDelCurso.findIndex(id => id == selectedLessonId);
        if (currentIndex === -1) {
            console.warn('⚠️ Lección actual no encontrada en ModuloContenido');
            return;
        }

        let nextLessonId = null;

        if (direction === 'prev') {
            if (currentIndex > 0) {
                nextLessonId = todasLeccionesDelCurso[currentIndex - 1];
            }
        } else if (direction === 'next') {
            if (currentIndex < todasLeccionesDelCurso.length - 1) {
                nextLessonId = todasLeccionesDelCurso[currentIndex + 1];
            }
        }

        if (nextLessonId) {
            const proceedToNext = () => {
                setSelectedLessonId(nextLessonId);
                setSelectedSection(null);
                setSelectedColumn(null);
                setSelectedElement(null);

                navigate(location.pathname, {
                    replace: true,
                    state: { ...location.state, lessonId: nextLessonId }
                });
            };

            if (isDirty || isAutoSaving || isSavingRef.current) {
                triggerConfirm({
                    title: 'Cambios sin guardar',
                    message: "⚠️ Tienes cambios sin guardar o el proceso de guardado no ha terminado. ¿Estás seguro de que quieres cambiar a otra lección?",
                    onConfirm: proceedToNext,
                    variant: 'warning'
                });
            } else {
                proceedToNext();
            }
        }
    };

    // Helper para obtener información de navegación
    const getNavigationInfo = () => {
        if (!selectedLessonId) return { canGoPrev: false, canGoNext: false, currentIndex: -1, total: 0 };

        const currentLesson = lessons.find(l => (l.idLeccion || l.IdLeccion) == selectedLessonId);
        if (!currentLesson) return { canGoPrev: false, canGoNext: false, currentIndex: -1, total: 0 };

        // Obtener el módulo de la lección actual para saber el curso
        const currentModule = modulos.find(m => (m.idModulo || m.IdModulo) === (currentLesson.idModulo || currentLesson.IdModulo));
        if (!currentModule) return { canGoPrev: false, canGoNext: false, currentIndex: -1, total: 0 };

        // Obtener todos los módulos del curso actual, ordenados
        // Obtener todos los módulos del curso actual, ordenados
        const currentCourseModules = modulos
            .filter(m => (m.idCurso || m.IdCurso) === (currentModule.idCurso || currentModule.IdCurso))
            .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

        // Obtener TODAS las lecciones del curso en orden
        const todasLeccionesDelCurso = [];

        for (const modulo of currentCourseModules) {
            const leccionesDelModulo = moduloContenido
                .filter(mc => {
                    const idModulo = mc.idModulo || mc.IdModulo;
                    const tablaContenido = mc.tablaContenido || mc.TablaContenido;
                    const activo = mc.activo !== undefined ? mc.activo : mc.Activo;

                    const isCorrectModule = idModulo === (modulo.idModulo || modulo.IdModulo);
                    const isLesson = tablaContenido && tablaContenido.toLowerCase() === 'lecciones';
                    const isActive = activo;
                    return isCorrectModule && isLesson && isActive;
                })
                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0))
                .map(mc => mc.idAsociado || mc.IdAsociado);

            todasLeccionesDelCurso.push(...leccionesDelModulo);
        }

        const currentIndex = todasLeccionesDelCurso.findIndex(id => id == selectedLessonId);

        return {
            canGoPrev: currentIndex > 0,
            canGoNext: currentIndex >= 0 && currentIndex < todasLeccionesDelCurso.length - 1,
            currentIndex,
            total: todasLeccionesDelCurso.length
        };
    };

    return (
        <div
            className="page-builder-wrapper"
            style={resizing ? { userSelect: 'none', cursor: 'grabbing' } : {}}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
        >
            {/* Header reutilizando el estilo de CoursePlayerPreview */}
            <header className="cp-topbar" style={{ position: 'relative' }}>
                <button
                    type="button"
                    className="cp-topbar-close"
                    onClick={handleBack}
                    title="Regresar al currículum"
                >
                    <FiChevronLeft />
                </button>

                <div className="cp-topbar-title" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'max-content'
                }}>

                    {/* Botón Anterior */}
                    {(() => {
                        const navInfo = getNavigationInfo();
                        return (
                            <button
                                className="action-btn"
                                onClick={() => handleNavigateLesson('prev')}
                                disabled={!navInfo.canGoPrev}
                                style={{
                                    padding: '6px 14px',
                                    fontSize: '0.85rem',
                                    background: 'var(--btn-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '9999px',
                                    cursor: 'pointer',
                                    opacity: navInfo.canGoPrev ? 1 : 0.3,
                                    display: navInfo.canGoPrev ? 'block' : 'none',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                }}
                            >
                                ← Anterior
                            </button>
                        );
                    })()}

                    <span className="cp-topbar-course">
                        {lessonTitle || 'Lección'}
                    </span>

                    {/* Botón Siguiente */}
                    {(() => {
                        const navInfo = getNavigationInfo();
                        return (
                            <button
                                className="action-btn"
                                onClick={() => handleNavigateLesson('next')}
                                disabled={!navInfo.canGoNext}
                                style={{
                                    padding: '6px 14px',
                                    fontSize: '0.85rem',
                                    background: 'var(--btn-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '9999px',
                                    cursor: 'pointer',
                                    opacity: navInfo.canGoNext ? 1 : 0.3,
                                    display: navInfo.canGoNext ? 'block' : 'none',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                }}
                            >
                                Siguiente →
                            </button>
                        );
                    })()}
                </div>

                <div className="cp-topbar-close cp-topbar-close-right" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '15px', minWidth: '120px', justifyContent: 'flex-end' }}>
                    {isAutoSaving ? (
                        <span style={{ color: '#64748b', fontSize: '0.85rem', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                            Guardando...
                        </span>
                    ) : (
                        <span style={{ color: '#10b981', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            ✓ Guardado
                        </span>
                    )}
                </div>
            </header>

            <div className={"page-builder-container" + (propertiesOpen ? "" : " page-builder-container--props-collapsed")}>
                {/* Sidebar de Herramientas */}
                <div className="builder-sidebar">
                    <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666' }}>Secciones</h4>
                        <div className="tool-btn" onClick={() => addSection(1)} title="Sección 1 Columna">
                            <span className="tool-icon">▭</span>
                            <span>1 Columna</span>
                        </div>
                        <div className="tool-btn" onClick={() => addSection(2)} title="Sección 2 Columnas">
                            <span className="tool-icon">▭▭</span>
                            <span>2 Columnas</span>
                        </div>
                    </div>

                    <div>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666' }}>Elementos</h4>
                        <div className="tool-btn" onClick={() => addElementToColumn('h1')} title="Título H1">
                            <span className="tool-icon" style={{ fontWeight: 'bold' }}>H1</span>
                            <span>Título</span>
                        </div>
                        <div className="tool-btn" onClick={() => addElementToColumn('h2')} title="Subtítulo H2">
                            <span className="tool-icon" style={{ fontWeight: 'bold', fontSize: '1rem' }}>H2</span>
                            <span>Subtítulo</span>
                        </div>
                        <div className="tool-btn" onClick={() => addElementToColumn('text')} title="Agregar Texto">
                            <span className="tool-icon">T</span>
                            <span>Texto</span>
                        </div>
                        <div className="tool-btn" onClick={() => addElementToColumn('image')} title="Agregar Imagen">
                            <span className="tool-icon">🖼️</span>
                            <span>Imagen</span>
                        </div>
                        <div className="tool-btn" onClick={() => addElementToColumn('video')} title="Agregar Video">
                            <span className="tool-icon">🎬</span>
                            <span>Video</span>
                        </div>
                        <div className="tool-btn" onClick={() => addElementToColumn('iframe')} title="Agregar Sitio Web / Iframe">
                            <span className="tool-icon">🌐</span>
                            <span>Sitio Web</span>
                        </div>
                    </div>
                </div>

                {/* Canvas */}
                <div className="builder-canvas-wrapper" style={{ flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#f5f5f5' }}>
                    <div ref={canvasRef} style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Cargando...</div>}

                        {!selectedLessonId && !loading && (
                            <div style={{
                                textAlign: 'center',
                                padding: '60px 40px',
                                color: '#ef4444',
                                border: '2px dashed #ef4444',
                                borderRadius: '8px',
                                backgroundColor: '#fee2e2'
                            }}>
                                <h3 style={{ margin: '0 0 10px 0' }}>⚠️ No hay lección seleccionada</h3>
                                <p style={{ margin: 0 }}>
                                    {lessons.length === 0
                                        ? 'Crea una nueva lección usando el botón "+ Nueva" arriba'
                                        : 'Selecciona una lección del menú desplegable arriba'}
                                </p>
                            </div>
                        )}

                        {selectedLessonId && sections.length === 0 && !loading && (
                            <div
                                onDoubleClick={() => addSection(1)}
                                style={{ textAlign: 'center', padding: '40px', color: '#999', border: '2px dashed #ddd', borderRadius: '8px', cursor: 'pointer' }}
                                title="Doble click para agregar sección rápida"
                            >
                                Agrega una sección para comenzar (Doble click para agregar rápida)
                            </div>
                        )}

                        {sections.map((section) => (
                            <div
                                key={section.id}
                                className="lessons-section-container" // Clase añadida para referencia
                                onDragOver={(e) => handleSectionDragOver(e, section.id)}
                                onDrop={(e) => handleSectionDrop(e, section.id)}
                                style={{
                                    marginBottom: '20px',
                                    backgroundColor: section.style?.backgroundColor || '#ffffff',
                                    padding: `${section.style?.padding || 30}px`,
                                    borderRadius: '24px',
                                    border: selectedSection === section.id ? '2px solid var(--color-primary)' : '1px solid transparent',
                                    cursor: 'default',
                                    position: 'relative',
                                    opacity: draggingSection === section.id ? 0.6 : 1,
                                    boxShadow: dragOverSection === section.id
                                        ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                    transition: 'box-shadow 0.3s ease, border-color 0.2s ease, transform 0.2s ease'
                                }}
                                onClick={(e) => {
                                    setSelectedSection(section.id);
                                    if (selectedColumn === null || selectedColumn >= section.columns) {
                                        setSelectedColumn(0);
                                    }
                                    setSelectedElement(null);
                                }}
                            >
                                {/* Drag Handle */}
                                <div
                                    draggable
                                    onDragStart={(e) => handleSectionDragStart(e, section.id)}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        left: '10px',
                                        background: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        cursor: 'move',
                                        fontSize: '0.75rem',
                                        zIndex: 10,
                                        userSelect: 'none'
                                    }}
                                    title="Arrastra para reordenar"
                                >
                                    ⋮⋮
                                </div>
                                <button
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        await deleteSection(section.id);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        zIndex: 10
                                    }}
                                >
                                    ✕
                                </button>



                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: section.columns === 2
                                        ? `${section.splitRatio || 50}fr ${100 - (section.splitRatio || 50)}fr`
                                        : '1fr',
                                    gap: `${section.style?.gap || 20}px`
                                }}>
                                    {(section.children || []).map((column, colIdx) => (
                                        <div
                                            key={colIdx}
                                            onClick={(e) => {
                                                setSelectedSection(section.id);
                                                setSelectedColumn(colIdx);
                                                setSelectedElement(null);
                                            }}
                                            style={{
                                                minWidth: '0', // Vital para que CSS Grid funcione con contenido ancho
                                                minHeight: '100px',
                                                padding: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                border: selectedSection === section.id && selectedColumn === colIdx
                                                    ? '2px dashed transparent'
                                                    : '2px dashed transparent',
                                                borderRadius: '4px',
                                                backgroundColor: 'rgba(255,255,255,0.5)'
                                            }}
                                            onContextMenu={(e) => handleContextMenu(e, section.id, colIdx)}
                                        >
                                            {(!column.children || column.children.length === 0) && (
                                                <div style={{ textAlign: 'center', color: '#999', fontSize: '0.85rem', padding: '20px' }}>
                                                    Columna vacía - Agrega elementos
                                                </div>
                                            )}
                                            {(column.children || []).map((element) => (
                                                <div
                                                    key={element.id}
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Evitar que el click se propague a la columna
                                                        setSelectedSection(section.id);
                                                        setSelectedColumn(colIdx);
                                                        setSelectedElement(element.id);
                                                    }}
                                                    onContextMenu={(e) => handleElementContextMenu(e, element.id, section.id, colIdx)}
                                                    style={{
                                                        ...element.style,
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        border: selectedElement === element.id ? '2px solid #10b981' : '2px solid transparent',
                                                        cursor: 'default',
                                                        position: 'relative',
                                                        boxSizing: 'border-box',
                                                        userSelect: 'none',
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {element.type === 'text' || element.type === 'h1' || element.type === 'h2' ? (
                                                        <div style={{ userSelect: 'none', overflowWrap: 'break-word', wordWrap: 'break-word', maxWidth: '100%', whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: (element.content || '').replace(/\n/g, '<br />') }} />
                                                    ) : element.type === 'image' ? (
                                                        <img src={element.src} alt="Element" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : element.type === 'video' ? (
                                                        element.src ? (
                                                            <video
                                                                src={element.src.startsWith('http') ? element.src : `${SERVER_URL}${element.src}`}
                                                                controls
                                                                style={{ width: '100%', height: 'auto', borderRadius: '4px', backgroundColor: '#000' }}
                                                            />
                                                        ) : (
                                                            <div style={{ backgroundColor: '#000', height: '200px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                                                <span style={{ fontSize: '1.2rem' }}>Selecciona un video…</span>
                                                            </div>
                                                        )) : element.type === 'iframe' ? (
                                                            <div
                                                                className={`element-content element-iframe ${selectedElement === element.id ? 'selected' : ''}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedElement(element.id);
                                                                    setSelectedSection(section.id);
                                                                    setSelectedColumn(colIdx);
                                                                }}
                                                                onContextMenu={(e) => handleContextMenu(e, 'element', element.id)}
                                                                style={{
                                                                    ...element.style,
                                                                    position: 'relative',
                                                                    width: '100%',
                                                                    minHeight: '100px', // Mínimo para ver algo
                                                                    backgroundColor: '#f3f4f6'
                                                                }}
                                                            >
                                                                {element.src ? (
                                                                    <>
                                                                        {/* Overlay transparente para permitir selección cuando no se está interactuando */}
                                                                        <div
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                left: 0,
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                display: (selectedElement === element.id && !resizing) ? 'none' : 'block', // Ocultar overlay al seleccionar para permitir interacción básica si se desea, o mantenerlo para resizing.
                                                                                // Mejor enfoque: Siempre mostrar overlay de "selección" transparente, pero si está seleccionado, tal vez queramos un botón de "interactuar".
                                                                                // Por ahora: overlay invisible que captura clicks para selección.
                                                                                cursor: 'pointer',
                                                                                zIndex: 1
                                                                            }}
                                                                        />
                                                                        <iframe
                                                                            key={element.src}
                                                                            src={formatUrl(element.src)}
                                                                            title="Embedded Content"
                                                                            style={{
                                                                                width: '100%',
                                                                                height: element.style.height || '400px',
                                                                                border: 'none',
                                                                                pointerEvents: (selectedElement === element.id && !resizing) ? 'auto' : 'none',
                                                                                display: 'block'
                                                                            }}
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                            allowFullScreen
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                                                        <FiGlobe size={40} />
                                                                        <p>Sitio Web / Iframe sin configurar</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : null}

                                                    {/* Handles de resize */}
                                                    {selectedElement === element.id && (
                                                        <>
                                                            {/* Lateral derecho (ancho) */}
                                                            <div
                                                                className="resize-handle handle-e"
                                                                onMouseDown={(e) => {
                                                                    e.stopPropagation();
                                                                    handleResizeStart(e, element.id, 'e');
                                                                }}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '50%',
                                                                    right: '-2px',
                                                                    transform: 'translateY(-50%)',
                                                                    width: '6px',
                                                                    height: '36px',
                                                                    backgroundColor: '#10b981',
                                                                    cursor: 'ew-resize',
                                                                    borderRadius: '4px',
                                                                    zIndex: 5
                                                                }}
                                                            />

                                                            {/* Resize Vertical e Inferior para Videos, Imágenes e Iframes */}
                                                            {(element.type === 'video' || element.type === 'image' || element.type === 'iframe') && (
                                                                <>
                                                                    {/* Inferior (alto) */}
                                                                    <div
                                                                        className="resize-handle handle-s"
                                                                        onMouseDown={(e) => {
                                                                            e.stopPropagation();
                                                                            handleResizeStart(e, element.id, 's');
                                                                        }}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: '-2px',
                                                                            left: '50%',
                                                                            transform: 'translateX(-50%)',
                                                                            width: '36px',
                                                                            height: '6px',
                                                                            backgroundColor: '#10b981',
                                                                            cursor: 'ns-resize',
                                                                            borderRadius: '4px',
                                                                            zIndex: 5
                                                                        }}
                                                                    />

                                                                    {/* Esquina inferior derecha */}
                                                                    <div
                                                                        className="resize-handle handle-se"
                                                                        onMouseDown={(e) => {
                                                                            e.stopPropagation();
                                                                            handleResizeStart(e, element.id, 'se');
                                                                        }}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: '-2px',
                                                                            right: '-2px',
                                                                            width: '10px',
                                                                            height: '10px',
                                                                            backgroundColor: '#10b981',
                                                                            cursor: 'nwse-resize',
                                                                            borderRadius: '50%',
                                                                            zIndex: 5
                                                                        }}
                                                                    />
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Panel de Propiedades */}
                <div className="lessons-properties-wrapper">
                    <div className="properties-panel">
                        <div className="panel-header">Propiedades</div>

                        {currentElement ? (
                            <>


                                {(currentElement.type === 'text' || currentElement.type === 'h1' || currentElement.type === 'h2') && (
                                    <div className="prop-group">
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                            <label className="prop-label" style={{ marginBottom: 0 }}>Contenido</label>
                                            <div className="tooltip-container">
                                                <span className="info-icon">?</span>
                                                <div className="tooltip-text">
                                                    <strong>Formato soportado:</strong><br />
                                                    &lt;b&gt;<b>Negrita</b>&lt;/b&gt;<br />
                                                    &lt;i&gt;<i>Cursiva</i>&lt;/i&gt;<br />
                                                    &lt;br&gt; Salto de línea
                                                </div>
                                            </div>
                                        </div>
                                        <textarea
                                            className="prop-input"
                                            rows={4}
                                            value={currentElement.content}
                                            onChange={(e) => updateElement(currentElement.id, { content: e.target.value })}
                                            placeholder="Ej: Hola <b>Mundo</b>"
                                        />
                                    </div>
                                )}

                                {(currentElement.type === 'image' || currentElement.type === 'video' || currentElement.type === 'iframe') && (
                                    <>

                                        {currentElement.type === 'image' && (
                                            <div className="prop-group">
                                                <button
                                                    type="button"
                                                    className="action-btn"
                                                    onClick={() => {
                                                        setImageTargetElementId(currentElement.id);
                                                        setImageModalOpen(true);
                                                    }}
                                                >
                                                    📷 Subir / cambiar imagen
                                                </button>
                                            </div>
                                        )}

                                        {currentElement.type === 'video' && (
                                            <div className="prop-group">
                                                <button
                                                    type="button"
                                                    className="action-btn"
                                                    onClick={() => {
                                                        setVideoTargetElementId(currentElement.id);
                                                        setVideoModalOpen(true);
                                                    }}
                                                >
                                                    🎬 Subir / cambiar video
                                                </button>
                                            </div>
                                        )}

                                        {currentElement.type === 'iframe' && (
                                            <div className="prop-group">
                                                <button
                                                    type="button"
                                                    className="action-btn"
                                                    onClick={() => {
                                                        setUrlTargetElementId(currentElement.id);
                                                        setUrlModalOpen(true);
                                                    }}
                                                >
                                                    <FiEdit2 /> Cambiar URL
                                                </button>
                                            </div>
                                        )}

                                        <div className="prop-group">
                                            <label className="prop-label">Dimensiones (Informativo)</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <div>
                                                    <span style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Alto:</span>
                                                    <input
                                                        className="prop-input"
                                                        type="text"
                                                        value={currentElement.style.height || 'Auto'}
                                                        readOnly
                                                        disabled
                                                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', width: '100%' }}
                                                    />
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Ancho:</span>
                                                    <input
                                                        className="prop-input"
                                                        type="text"
                                                        value={currentElement.style.width || 'Auto'}
                                                        readOnly
                                                        disabled
                                                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', width: '100%' }}
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="action-btn"
                                                style={{ marginTop: '10px', backgroundColor: '#64748b' }}
                                                onClick={() => updateElementStyle(currentElement.id, { width: '100%', height: currentElement.type === 'image' ? '200px' : (currentElement.type === 'iframe' ? '400px' : 'auto') })}
                                            >
                                                ↺ Resetear Tamaño
                                            </button>
                                        </div>
                                    </>
                                )}

                                <div className="panel-header" style={{ marginTop: '1rem' }}>Estilos</div>



                                {(currentElement.type === 'text' || currentElement.type === 'h1' || currentElement.type === 'h2') && (
                                    <>
                                        <div className="prop-group">
                                            <label className="prop-label">Color Texto</label>
                                            <input
                                                className="prop-input"
                                                type="color"
                                                value={currentElement.style.color}
                                                onChange={(e) => updateElementStyle(currentElement.id, { color: e.target.value })}
                                                style={{ height: '30px', width: '100%' }}
                                            />
                                        </div>

                                        <div className="prop-group">
                                            <label className="prop-label">Tipografía</label>
                                            <select
                                                className="prop-input"
                                                value={currentElement.style.fontFamily || 'Inter, sans-serif'}
                                                onChange={(e) => updateElementStyle(currentElement.id, { fontFamily: e.target.value })}
                                            >
                                                <option value="Inter, sans-serif">Inter (Predeterminada)</option>
                                                <option value="Arial, sans-serif">Arial</option>
                                                <option value="Verdana, sans-serif">Verdana</option>
                                                <option value="'Times New Roman', serif">Times New Roman</option>
                                                <option value="Georgia, serif">Georgia</option>
                                                <option value="'Courier New', monospace">Courier New</option>
                                                <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                                                <option value="Impact, fantasy">Impact</option>
                                                <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                                            </select>
                                        </div>

                                        <div className="prop-group">
                                            <label className="prop-label">Tamaño</label>
                                            <input
                                                className="prop-input"
                                                type="number"
                                                min="1"
                                                max="250"
                                                value={currentElement.style.fontSize || 16}
                                                onChange={(e) => {
                                                    const val = Math.min(250, parseInt(e.target.value) || 0);
                                                    updateElementStyle(currentElement.id, { fontSize: val });
                                                }}
                                            />
                                        </div>

                                    </>
                                )}

                                <button
                                    className="btn-action"
                                    style={{
                                        width: '100%',
                                        marginTop: '20px'
                                    }}
                                    onClick={() => {
                                        deleteElement(currentElement.id);
                                    }}
                                >
                                    Eliminar elemento
                                </button>
                            </>
                        ) : (
                            <div style={{ color: '#888', textAlign: 'center', marginTop: '2rem' }}>
                                {selectedSection ? 'Selecciona un elemento para editar' : 'Selecciona una sección'}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className={
                            "lessons-properties-toggle" +
                            (propertiesOpen ? "" : " lessons-properties-toggle--collapsed")
                        }
                        onClick={() => {
                            // Solo permitir colapsar en pantallas pequeñas
                            if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                                setPropertiesOpen(prev => !prev);
                            }
                        }}
                        aria-label={
                            propertiesOpen
                                ? 'Ocultar panel de propiedades'
                                : 'Mostrar panel de propiedades'
                        }
                    >
                        <span className="lessons-properties-toggle-icon">
                            {propertiesOpen ? '›' : '‹'}
                        </span>
                    </button>
                </div>
            </div>

            {/* Modal para crear lección */}
            {
                showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            width: '400px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ marginTop: 0 }}>Nueva Lección</h3>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Título:</label>
                                <input
                                    type="text"
                                    value={newLesson.titulo}
                                    onChange={(e) => setNewLesson({ ...newLesson, titulo: e.target.value })}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Módulo:</label>
                                <select
                                    value={newLesson.idModulo}
                                    onChange={(e) => setNewLesson({ ...newLesson, idModulo: e.target.value })}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                >
                                    {modulos.length === 0 && <option value="">-- No hay módulos disponibles --</option>}
                                    {modulos.map(m => (
                                        <option key={m.idModulo} value={m.idModulo}>{m.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Monedas (Recompensa):</label>
                                <input
                                    type="number"
                                    value={newLesson.monedas}
                                    onChange={(e) => setNewLesson({ ...newLesson, monedas: e.target.value })}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#e5e7eb',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateLesson}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Crear
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal para subir video de elemento tipo "video" */}
            <VideoUploadModal
                open={videoModalOpen}
                mode={videoTargetElementId ? 'edit' : 'new'}
                initialSrc={videoTargetElementId ? findElementById(videoTargetElementId)?.src || '' : ''}
                onClose={() => {
                    setVideoModalOpen(false);
                    setVideoTargetElementId(null);
                }}
                onUploaded={handleVideoUploaded}
            />

            {/* Modal para subir imagen de elemento tipo "image" */}
            <ImageUploadModal
                open={imageModalOpen}
                mode={imageTargetElementId ? 'edit' : 'new'}
                initialSrc={imageTargetElementId ? findElementById(imageTargetElementId)?.src || '' : ''}
                onClose={() => {
                    setImageModalOpen(false);
                    setImageTargetElementId(null);
                }}
                onUploaded={handleImageUploaded}
            />


            {/* Custom Context Menu */}
            {contextMenu.visible && (
                <div
                    ref={contextMenuRef}
                    style={{
                        position: 'fixed',
                        top: contextMenu.y,
                        left: contextMenu.x,
                        backgroundColor: 'white',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 0',
                        zIndex: 9999,
                        minWidth: '200px',
                        border: '1px solid #e5e7eb'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {contextMenu.type === 'element' ? (() => {
                        const element = findElementById(contextMenu.elementId);
                        if (!element) return null;
                        return (
                            <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '280px' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '5px' }}>
                                    Editar {element.type === 'h1' ? 'Título' : element.type === 'h2' ? 'Subtítulo' : element.type === 'image' ? 'Imagen' : element.type === 'video' ? 'Video' : 'Texto'}
                                </div>

                                {(element.type === 'text' || element.type === 'h1' || element.type === 'h2') && (
                                    <>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Contenido</label>
                                            <textarea
                                                value={element.content}
                                                onChange={(e) => updateElement(element.id, { content: e.target.value })}
                                                style={{ width: '100%', fontSize: '0.85rem', minHeight: '60px', resize: 'vertical', padding: '5px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                                placeholder="Escribe aquí..."
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Color</label>
                                                <input
                                                    type="color"
                                                    value={element.style.color}
                                                    onChange={(e) => updateElementStyle(element.id, { color: e.target.value })}
                                                    style={{ width: '100%', height: '35px', padding: 0, border: 'none', cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Tamaño (px)</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="250"
                                                    value={element.style.fontSize}
                                                    onChange={(e) => {
                                                        const val = Math.min(250, parseInt(e.target.value) || 0);
                                                        updateElementStyle(element.id, { fontSize: val });
                                                    }}
                                                    style={{ width: '100%', padding: '5px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Tipografía</label>
                                            <select
                                                value={element.style.fontFamily || 'Inter, sans-serif'}
                                                onChange={(e) => updateElementStyle(element.id, { fontFamily: e.target.value })}
                                                style={{ width: '100%', padding: '5px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                            >
                                                <option value="Inter, sans-serif">Inter</option>
                                                <option value="Arial, sans-serif">Arial</option>
                                                <option value="Verdana, sans-serif">Verdana</option>
                                                <option value="'Times New Roman', serif">Times New Roman</option>
                                                <option value="Georgia, serif">Georgia</option>
                                                <option value="'Courier New', monospace">Courier New</option>
                                            </select>
                                        </div>
                                    </>
                                )}

                                {(element.type === 'image' || element.type === 'video' || element.type === 'iframe') && (
                                    <>
                                        <div>
                                            <button
                                                type="button"
                                                className="action-btn"
                                                style={{ width: '100%', marginBottom: '6px' }}
                                                onClick={() => {
                                                    if (element.type === 'image') {
                                                        setImageTargetElementId(element.id);
                                                        setImageModalOpen(true);
                                                    } else if (element.type === 'video') {
                                                        setVideoTargetElementId(element.id);
                                                        setVideoModalOpen(true);
                                                    } else if (element.type === 'iframe') {
                                                        setUrlTargetElementId(element.id);
                                                        setUrlModalOpen(true);
                                                    }
                                                    setContextMenu({ ...contextMenu, visible: false });
                                                }}
                                            >
                                                {element.type === 'image' ? '📷 Cambiar Imagen' : element.type === 'video' ? '🎬 Cambiar Video' : '🌐 Cambiar URL'}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="action-btn"
                                            style={{
                                                width: '100%',
                                                backgroundColor: '#64748b',
                                                boxShadow: '0 4px 6px -1px rgba(71,85,105,0.3)'
                                            }}
                                            onClick={() => updateElementStyle(element.id, { width: '100%', height: 'auto' })}
                                        >
                                            ↺ Resetear Tamaño
                                        </button>
                                    </>
                                )}

                                <button
                                    type="button"
                                    className="delete-btn"
                                    style={{
                                        width: '100%',
                                        marginTop: '8px',
                                        background: 'var(--gradient-primary)',
                                        color: 'white',
                                        border: 'none',
                                        textTransform: 'none',
                                        letterSpacing: 'normal'
                                    }}
                                    onClick={() => {
                                        deleteElement(element.id);
                                        setContextMenu({ ...contextMenu, visible: false });
                                    }}
                                >
                                    🗑️ Eliminar elemento
                                </button>
                            </div>
                        );
                    })() : (
                        <>
                            <div style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', marginBottom: '0.25rem' }}>
                                Agregar Elemento
                            </div>
                            {[
                                { type: 'h1', label: 'Título Principal (H1)', icon: 'H1' },
                                { type: 'h2', label: 'Subtítulo (H2)', icon: 'H2' },
                                { type: 'text', label: 'Texto', icon: 'T' },
                                { type: 'image', label: 'Imagen', icon: '🖼️' },
                                { type: 'video', label: 'Video', icon: '🎬' },
                                { type: 'iframe', label: 'Sitio Web', icon: '🌐' },
                            ].map((item) => (
                                <div
                                    key={item.type}
                                    onClick={() => {
                                        addElementToColumn(item.type);
                                        setContextMenu({ ...contextMenu, visible: false });
                                    }}
                                    className="context-menu-item"
                                    style={{
                                        padding: '0.5rem 1rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        color: '#374151',
                                        fontSize: '0.9rem',
                                        transition: 'background-color 0.1s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                    <span style={{ fontWeight: 600, width: '20px', textAlign: 'center' }}>{item.icon}</span>
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
            {/* Botón FAB para Observaciones */}
            <button
                className="fab-review"
                onClick={() => setIsObservacionesModalOpen(true)}
                title="Ver observaciones"
            >
                <FiMessageSquare />
            </button>

            {/* Modal de Observaciones */}
            <ObservacionesModal
                isOpen={isObservacionesModalOpen}
                onClose={() => setIsObservacionesModalOpen(false)}
                idCurso={location.state?.idCurso || (() => {
                    const l = lessons.find(x => x.idLeccion == selectedLessonId);
                    const m = l && modulos.find(y => y.idModulo === l.idModulo);
                    return m?.idCurso || m?.IdCurso;
                })()}
                currentContentId={selectedLessonId}
                currentContentType="LECCION"
                onObservationAdded={() => { }}
                readOnly={true}
            />
            <UrlInputModal
                isOpen={urlModalOpen}
                onClose={() => setUrlModalOpen(false)}
                onConfirm={(url, title) => {
                    if (urlTargetElementId) {
                        updateElement(urlTargetElementId, {
                            src: url,
                            content: title
                        });

                        setTimeout(() => {
                            updateElementStyle(urlTargetElementId, {
                                height: '400px'
                            });
                        }, 0);
                    }
                }}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                buttonText={alertConfig.confirmText}
                cancelText={alertConfig.cancelText}
                onClose={() => {
                    setAlertConfig(prev => ({ ...prev, open: false }));
                    if (alertConfig.onConfirm) alertConfig.onConfirm();
                }}
                onCancel={alertConfig.onCancel ? () => {
                    setAlertConfig(prev => ({ ...prev, open: false }));
                    alertConfig.onCancel();
                } : null}
            />
        </div >
    );
};

export default Lessons;
